// ─────────────────────────────────────────────────────────────
//  Struktur-Konfiguration (Kunden-Pipeline)
//
//  Hier wird die komplette Pipeline definiert: PHASEN -> ÜBERSTATUS -> SUBSTATUS.
//  Das Struktur-Board (StructureBoard.js) rendert sich vollständig aus dieser
//  Datei – neue Phasen/Status hinzufügen heißt einfach hier editieren.
//
//  Begriffe:
//   - PHASE       = farbige Gruppe (z. B. Sales-Phase = bläulich). Klammert mehrere Überstatus.
//   - ÜBERSTATUS  = eine Spalte im Board. Enthält alle Kunden mit diesem Überstatus.
//   - SUBSTATUS   = feiner Status innerhalb einer Spalte (Badge/Dropdown auf der Karte).
//
//  HINWEIS: Das ist ein sinnvoller Start-Aufbau. Die exakten Phasen/Status
//  liefert das Team noch nach – dann hier ergänzen/umbenennen.
// ─────────────────────────────────────────────────────────────

import { ACCOUNT_SETUP_PHASES } from '../Protocool/accountSetupConfig';

export const PHASES = [
  {
    id: 'sales',
    label: 'Sales-Phase',
    // bläulich – gedämpftes Periwinkle/Azur
    accent: '#7c9df0',
    accentSoft: 'rgba(124, 157, 240, 0.04)',
    superStatuses: [
      {
        id: 'kennenlern',
        label: 'Kennenlern-Phase',
        // Kein eigener Substatus mehr: Der Fortschritt in der Kennenlern-Phase
        // ergibt sich bereits aus Status + Checkliste (und dem Auto-Move in den
        // Präsentations-Call, sobald ein Termin feststeht). Substatus gibt es nur
        // noch in der Lost-Phase, wo der konkrete Grund echte Info liefert.
        subStatuses: [],
      },
      {
        id: 'praesentation',
        label: 'Präsentations-Call',
        subStatuses: [],
      },
    ],
  },
  {
    id: 'preparation',
    label: 'Vorbereitungsphase',
    // gelblich – warmes, gedecktes Gold statt grellem Gelb
    accent: '#d8a85a',
    accentSoft: 'rgba(216, 168, 90, 0.04)',
    superStatuses: [
      {
        id: 'angebot_unterzeichnet',
        label: 'Angebot gesendet',
        subStatuses: [],
      },
      {
        id: 'setup',
        label: 'Setup',
        subStatuses: [],
      },
      {
        id: 'kickoff',
        label: 'Kick-off-Call',
        subStatuses: [],
      },
    ],
  },
  {
    id: 'operative',
    label: 'Aktive Phase',
    // blau + grün – ruhiges Emerald/Teal
    accent: '#4cb89a',
    accentSoft: 'rgba(76, 184, 154, 0.04)',
    superStatuses: [
      {
        id: 'experimental',
        label: 'Experimentalphase',
        subStatuses: [],
      },
      {
        id: 'erster_kunde',
        label: 'Erster Kunde generiert',
        subStatuses: [],
      },
    ],
  },
];

// Der finale Status: sobald ein Kontakt diesen erreicht, gilt "erster Kunde generiert".
// Wird fürs Zeit-Tracking (Erstkontakt -> erster Kunde) verwendet.
export const FINAL_SUPER_STATUS = 'erster_kunde';

// Die Sales-Phase (Kennenlern + Präsentations-Call). Der Präsentationstermin
// ist nur hier relevant – ab der Vorbereitungsphase liegt er ohnehin in der
// Vergangenheit und der Countdown wäre nur noch Lärm.
export const SALES_PHASE_ID = PHASES[0].id; // 'sales'
// Überstatus "Präsentations-Call": hier landet automatisch jeder Kontakt mit
// fixem Präsentationstermin (siehe Auto-Move im StructureBoard).
export const PRESENTATION_SUPER_STATUS = PHASES[0].superStatuses[1].id; // 'praesentation'
// Überstatus "Angebot unterzeichnet": hier bieten wir den Shortcut zum Generieren
// der "Letzte-Infos-vor-dem-Kick-Off"-E-Mail an (Karte + Detail-Drawer).
export const OFFER_SIGNED_SUPER_STATUS = PHASES[1].superStatuses[0].id; // 'angebot_unterzeichnet'
// Vorbereitungsphase (gelb): Angebot unterzeichnet -> Setup -> Kick-off-Call.
// Hier läuft der Kick-off-Countdown; der Präsentationstermin ist Geschichte.
export const PREPARATION_PHASE_ID = PHASES[1].id; // 'preparation'
// Operative Phase (Experimentalphase -> erster Kunde).
export const OPERATIVE_PHASE_ID = PHASES[2].id; // 'operative'
// Überstatus "Setup": technisches Setup + Team-Briefing werden erledigt, während
// der Countdown bis zum Kick-off-Call läuft.
export const SETUP_SUPER_STATUS = PHASES[1].superStatuses[1].id; // 'setup'
// Überstatus "Kick-off-Call": Sobald der Kick-off-Termin erreicht ist, springt der
// Kontakt automatisch hierher. Damit er nicht "feststeckt", gibt es Quick-Actions
// (siehe QUICK_ACTIONS.kickoff), die ihn mit einem Klick weiterbewegen.
export const KICKOFF_SUPER_STATUS = PHASES[1].superStatuses[2].id; // 'kickoff'

// ─────────────────────────────────────────────────────────────
//  QUICK-ACTIONS (Shortcuts direkt auf der Karte)
//
//  Kontextabhängige nächste Schritte je Überstatus: ein Klick verschiebt den
//  Kontakt in den Ziel-Überstatus (to) – ohne den Drawer öffnen zu müssen.
//  So kann das Team direkt loslegen, je nachdem in welcher Phase/Status man ist.
//  tone steuert die Optik: 'primary' (Vorwärts), 'lost' (Abbruch), 'ghost'.
// ─────────────────────────────────────────────────────────────
export const QUICK_ACTIONS = {
  // Präsentations-Call: erscheint nur, wenn der Termin abgelaufen ist
  // (Trigger siehe StructureBoard). Vorwärts ins Angebot oder ab nach Lost.
  praesentation: [
    { id: 'pres_to_offer', label: 'Ich habe ein Angebot erstellt und versendet', to: 'angebot_unterzeichnet', tone: 'primary' },
    { id: 'pres_to_lost', label: 'Zu Lost', to: 'lost_sales', tone: 'lost' },
  ],
  // Kick-off-Call: der Call hat hier bereits stattgefunden (das sagt der Status).
  // Kein "Zu Lost" – nach dem Kick-off gehen wir NICHT davon aus, dass der Kontakt
  // verloren ist. Stattdessen nur der Vorwärts-Schritt: Kontakt ist startklar und
  // wandert in die operative Phase. Ist er noch NICHT startklar, wird im Detail ein
  // selbst definierter nächster Schritt gesetzt + auf "Warten" gestellt (kein Move).
  kickoff: [
    { id: 'kickoff_to_experimental', label: 'Startklar – jetzt starten', to: 'experimental', tone: 'primary' },
  ],
};

export const quickActionsFor = (superStatusId) => QUICK_ACTIONS[superStatusId] || [];

// ─────────────────────────────────────────────────────────────
//  LOST-PHASE (separat, im Board horizontal ein-/ausklappbar)
//
//  Hierhin wandern Kontakte/Kunden, die nicht weitergemacht haben, verloren
//  gingen oder gekündigt haben. Die Spalten gruppieren grob, WO es endete; die
//  Substatus liefern den genauen, zu uns passenden Grund.
// ─────────────────────────────────────────────────────────────
export const LOST_PHASE = {
  id: 'lost',
  label: 'Lost-Phase',
  // gedeckte, entsättigte Terrakotta – signalisiert "verloren" ohne grell zu sein
  accent: '#c47b76',
  accentSoft: 'rgba(196, 123, 118, 0.04)',
  superStatuses: [
    {
      id: 'lost_sales',
      label: 'Im Sales verloren',
      subStatuses: [
        'Kein Budget / zu teuer',
        'Kein akuter Bedarf / Schmerz zu klein',
        'Preis-Einwand im Angebot',
        'Ghosting nach Erstkontakt',
        'Nicht zum Präsentations-Call erschienen',
        'Macht es lieber intern (inhouse)',
        'Wettbewerber gewählt',
        'Falscher Ansprechpartner / kein Entscheider',
        'Nicht im Zielkundenprofil (ICP)',
        'Timing passt nicht (später nochmal)',
        'Keine Antwort mehr',
      ],
    },
    {
      id: 'lost_pre_start',
      label: 'Vor Start abgesprungen',
      subStatuses: [
        'Angebot final abgelehnt',
        'Vertrag nicht unterschrieben',
        'Vor Kick-off abgesprungen',
        'Setup-Daten nie geliefert',
        'Setup-Fee nicht bezahlt',
        'Interne Freigabe gescheitert',
        'Kalte Füße / Rückzieher',
      ],
    },
    {
      id: 'lost_churn',
      label: 'Gekündigt / Churn',
      subStatuses: [
        'Reguläre Kündigung',
        'Zu wenig Ergebnisse / kein ROI',
        'Erwartungen nicht erfüllt',
        'Budget gestrichen / Sparmaßnahmen',
        'Ansprechpartner gewechselt',
        'Macht es jetzt intern',
        'Pausiert (will später weitermachen)',
        'Vertrag regulär ausgelaufen',
        'Insolvenz / Geschäftsaufgabe',
        'Zahlungsausfall',
      ],
    },
  ],
};

// Reguläre Phasen + Lost-Phase (Lost immer ganz hinten).
export const ALL_PHASES = [...PHASES, LOST_PHASE];

// Set der Lost-Überstatus-IDs (für schnelle Checks im Board).
export const LOST_SUPER_STATUS_IDS = LOST_PHASE.superStatuses.map((s) => s.id);
export const isLostSuperStatus = (superStatusId) => LOST_SUPER_STATUS_IDS.includes(superStatusId);

// Sentinel-Wert im Grund-Dropdown: wählt der Nutzer „Sonstiger Grund …", erscheint
// ein freies Textfeld. Der eingegebene Text wird ganz normal als Substatus gespeichert.
export const LOST_REASON_CUSTOM = '__custom__';

// Liefert true, wenn der gespeicherte Grund kein vordefinierter Substatus der Spalte
// ist (also ein frei eingegebener Custom-Grund) – steuert die Dropdown-Vorauswahl.
export const isCustomLostReason = (superStatusId, reason) => {
  if (!reason) return false;
  const col = LOST_PHASE.superStatuses.find((s) => s.id === superStatusId);
  return !col || !(col.subStatuses || []).includes(reason);
};

// Flache Liste aller Spalten (Überstatus) in Board-Reihenfolge, jeweils mit Phasenbezug.
// Inklusive Lost-Spalten, damit Status-Wechsel/Drawer-Dropdown sie kennen.
export const COLUMNS = ALL_PHASES.flatMap((phase) =>
  phase.superStatuses.map((s) => ({
    superStatusId: s.id,
    superStatusLabel: s.label,
    subStatuses: s.subStatuses || [],
    phaseId: phase.id,
    phaseLabel: phase.label,
    accent: phase.accent,
    accentSoft: phase.accentSoft,
  }))
);

// Defaults für ein frisch erzeugtes Kundenobjekt.
export const DEFAULT_PHASE = PHASES[0].id;
export const DEFAULT_SUPER_STATUS = PHASES[0].superStatuses[0].id;

// Welcher Phase gehört ein Überstatus an?
export function phaseForSuperStatus(superStatusId) {
  for (const phase of ALL_PHASES) {
    if (phase.superStatuses.some((s) => s.id === superStatusId)) return phase.id;
  }
  return DEFAULT_PHASE;
}

export function getColumn(superStatusId) {
  return COLUMNS.find((c) => c.superStatusId === superStatusId) || COLUMNS[0];
}

// ─────────────────────────────────────────────────────────────
//  Reihenfolge & abgeleitete Deal-Flags
//
//  Statt Flags ("Angebot gesendet", "Bezahlt") manuell zu pflegen, leiten wir sie
//  aus dem Pipeline-Fortschritt ab: Wer "Angebot unterzeichnet" erreicht hat, hat
//  ein Angebot bekommen; wer einen aktiven Vertrag hat, zahlt. So bleibt das Board
//  ehrlich und selbst-aktualisierend – ohne Häkchen-Pflege.
// ─────────────────────────────────────────────────────────────

// Reguläre Pipeline-Reihenfolge (ohne Lost) für Fortschritts-Vergleiche.
export const REGULAR_SUPER_STATUS_ORDER = PHASES.flatMap((p) => p.superStatuses.map((s) => s.id));

// Hat der Kontakt den Ziel-Überstatus mindestens erreicht (in der regulären Pipeline)?
export function hasReachedSuperStatus(currentId, targetId) {
  const cur = REGULAR_SUPER_STATUS_ORDER.indexOf(currentId);
  const tgt = REGULAR_SUPER_STATUS_ORDER.indexOf(targetId);
  return cur >= 0 && tgt >= 0 && cur >= tgt;
}

// Leitet die Deal-Flags aus dem Zustand ab – mit manuellem Override-Vorrang:
//   - offerSent: standardmäßig abgeleitet ("Angebot unterzeichnet" erreicht ODER
//                ein Angebot-Link hinterlegt). Ein manuell gesetztes Flag
//                (offerSentOverride = true/false) schlägt die Ableitung. So lässt
//                sich der Angebots-Status im Detail jederzeit wieder abtoggeln
//                (z. B. wenn ein Angebot doch nicht durchgeht), und mit null/undefined
//                fällt er zurück auf "automatisch".
//   - paid:      aktiver Vertrag (= Quelle der Wahrheit fürs Finanzen-Dashboard).
export function deriveDealFlags(customer) {
  const c = customer || {};
  const ss = c.superStatus || DEFAULT_SUPER_STATUS;
  const hasOfferUrl = typeof c.offerUrl === 'string' && c.offerUrl.trim().length > 0;
  const offerSentDerived = hasReachedSuperStatus(ss, OFFER_SIGNED_SUPER_STATUS) || hasOfferUrl;
  const offerSentOverridden = typeof c.offerSentOverride === 'boolean';
  const offerSent = offerSentOverridden ? c.offerSentOverride : offerSentDerived;
  const paid = !!(c.contract && c.contract.active);
  return { offerSent, offerSentDerived, offerSentOverridden, paid };
}

// Sinnvoller Default-Lost-Status, je nachdem wo der Kontakt gerade steht.
//   Sales-Phase        -> "Im Sales verloren"
//   Vorbereitungsphase -> "Vor Start abgesprungen"
//   Operative Phase    -> "Gekündigt / Churn"
export function suggestedLostStatus(superStatusId) {
  const ph = phaseForSuperStatus(superStatusId);
  if (ph === PREPARATION_PHASE_ID) return LOST_PHASE.superStatuses[1].id;
  if (ph === OPERATIVE_PHASE_ID) return LOST_PHASE.superStatuses[2].id;
  return LOST_PHASE.superStatuses[0].id;
}

// ─────────────────────────────────────────────────────────────
//  CHECKLISTEN pro Überstatus
//
//  Jede Phase/Spalte hat eigene Checklisten-Sektionen. Jedes Item:
//    { id, label, auto?(report) -> bool }
//  Wenn `auto` gesetzt ist, prüft die Funktion, ob die nötige Information
//  bereits in den eingehenden Report-Daten (API / Info-Dump + Research) steckt.
//  Dann wird das Häkchen beim Anlegen automatisch vorausgefüllt – der Rest wird
//  manuell abgehakt. So sehen wir immer: was haben wir, was fehlt noch.
//
//  HINWEIS: Start mit der Kennenlern-Phase (laut Screenshot). Weitere Phasen
//  bekommen ihre Checklisten, sobald das Team sie liefert.
// ─────────────────────────────────────────────────────────────

const hasText = (v) => typeof v === 'string' && v.trim().length > 0;
const hasItems = (v, min = 1) =>
  Array.isArray(v) && v.filter((x) => (typeof x === 'string' ? x.trim().length > 0 : x != null)).length >= min;
const anglesHaveContent = (r) =>
  Array.isArray(r?.research?.angles) && r.research.angles.some((a) => hasText(a?.title) || hasText(a?.body));
// Reporting (Experimentalphase) gilt als erledigt, sobald es ausdrücklich
// abgeschlossen wurde ODER eine der Antworten/Kennzahlen befüllt ist.
const reportingHasContent = (r) => {
  const rep = (r && r.reporting) || {};
  if (typeof rep.submittedAt === 'number') return true;
  const m = rep.metrics || {};
  return hasText(rep.summary) || hasText(rep.wins) || hasText(rep.challenges)
    || hasText(rep.learnings) || hasText(rep.recommendations)
    || hasText(m.contacted) || hasText(m.replies) || hasText(m.meetings);
};

export const CHECKLISTS = {
  kennenlern: [
    {
      title: 'Angebot des Kunden verstehen',
      items: [
        { id: 'was_verkauft', label: 'Was wird verkauft?', auto: (r) => hasText(r?.company?.industry) },
        { id: 'usp', label: "USP's / Differenzierung", auto: (r) => anglesHaveContent(r) || hasText(r?.verdict?.leverage?.positive) },
        { id: 'sales_aufstellung', label: 'Derzeitige Sales-Aufstellung' },
        { id: 'no_brainer', label: 'No-Brainer-Offer vorhanden?' },
      ],
    },
    {
      title: 'Fit prüfen',
      items: [
        { id: 'outreach_sinnvoll', label: 'Ist SalesGod-Outreach sinnvoll?', auto: (r) => hasText(r?.research?.coldOutreach?.headline) || hasText(r?.research?.coldOutreach?.assessment) },
        { id: 'ticket_size', label: 'Ticket Size / Umsatz ausreichend?', auto: (r) => typeof r?.config?.revenuePerClose === 'number' && r.config.revenuePerClose >= 1000 },
        { id: 'zielgruppe_def', label: 'Zielgruppe klar definierbar?', auto: (r) => hasText(r?.company?.audience) || hasItems(r?.research?.targetResearch?.ideas) },
        { id: 'zielgruppe_gross', label: 'Zielgruppe groß genug?', auto: (r) => hasItems(r?.research?.targetResearch?.ideas, 3) || hasItems(r?.research?.targetResearch?.revenueBands) },
      ],
    },
    {
      title: 'Erwartungshaltung & Aufgaben',
      items: [
        { id: 'kapazitaet', label: 'Kapazität für Sales-Calls vorhanden?' },
        { id: 'nachfrage', label: 'Echte Nachfrage (kein „magischer Lead-Hahn")' },
        { id: 'interesse_angebot', label: 'Interesse an einem Angebot?' },
      ],
    },
    {
      title: 'Next Steps',
      items: [
        { id: 'praesentation_organisiert', label: 'Präsentations-Termin organisiert' },
        { id: 'einladung_versendet', label: 'Termineinladung versendet' },
      ],
    },
  ],
  praesentation: [
    {
      title: 'Präsentations-Call',
      items: [
        { id: 'praes_erledigt', label: 'Präsentations-Call erledigt' },
        { id: 'angebot_gesendet', label: 'Angebot inkl. Call-Protokoll per Mail gesendet' },
        { id: 'kickoff_vereinbart', label: 'Kick-off-Call vereinbart' },
      ],
    },
  ],
  angebot_unterzeichnet: [
    {
      title: 'Angebot unterzeichnet',
      items: [
        { id: 'angebot_unterzeichnet', label: 'Angebot gesendet' },
        { id: 'letzte_fragen', label: 'Letzte Fragen beantwortet' },
      ],
    },
  ],
  setup: [
    {
      // Erster Punkt beim Setup: das Team briefen (E-Mail-Texting & Zielgruppe).
      // Bewusst manuell (eine aktive Handlung), nicht aus Daten ableitbar.
      title: 'Team-Briefing',
      items: [
        { id: 'setup_team_briefing', label: 'Infos ans Team gesendet: E-Mail-Texting & Zielgruppe' },
        // Wird automatisch abgehakt, sobald die Briefing-E-Mail an den Outreach-
        // Spezialisten generiert wurde. Manuell ein-/ausschaltbar; das Briefing
        // selbst bleibt unabhaengig vom Haken gespeichert.
        { id: 'setup_data_briefing', label: 'Daten-Briefing an Outreach-Spezialist erstellt' },
      ],
    },
    // Technisches SalesGod-Setup als 3-Phasen-Protokoll (Account-Setup-Protokoll).
    // Die Sektionen + Punkte werden aus ACCOUNT_SETUP_PHASES generiert, damit es
    // EINE Quelle der Wahrheit gibt: dieselben Punkte erscheinen in der Sales
    // Pipeline (hier), auf der Research-Seite (Setup-Tab) und auf der teilbaren
    // Account-Setup-Seite (/setup/:slug). Alle Punkte sind kundensichtbar
    // (shared: true) – abgehakt wird intern oder vom Setup-Manager über den Link.
    ...ACCOUNT_SETUP_PHASES.map((phase, i) => ({
      title: `Phase ${i + 1} · ${phase.label}${phase.duration ? ` (${phase.duration})` : ''}`,
      items: phase.steps.map((step) => ({
        id: step.id,
        label: step.title,
        shared: true,
        clientLabel: step.clientLabel || step.title,
      })),
    })),
  ],
  kickoff: [
    {
      title: 'Kick-off-Call',
      items: [
        { id: 'kickoff_durchgefuehrt', label: 'Kick-off-Call durchgeführt' },
        { id: 'kickoff_outreach_manager_vorgestellt', label: 'Kunde dem Outreach-Manager vorgestellt' },
        { id: 'kickoff_setup_getestet', label: 'Setup einmal komplett durchgetestet (E-Mails kommen sauber an)' },
        { id: 'kickoff_daten_an_outreach', label: 'Alle für den Outreach nötigen Daten vorhanden & an den Outreach-Manager gesendet' },
      ],
    },
  ],
  experimental: [
    {
      title: 'Experimentalphase',
      items: [
        // 30-Tage-Reporting des Outreach-Managers (eigene „Reporting"-Ansicht im
        // Research). Auto-Häkchen, sobald der Report Inhalt/Abschluss hat.
        { id: 'experimental_reporting', label: '30-Tage-Reporting vom Outreach-Manager ausgefüllt', auto: (r) => reportingHasContent(r) },
        { id: 'experimental_erledigt', label: 'Experimentalphase erledigt' },
      ],
    },
  ],
  erster_kunde: [
    {
      title: 'Erster Kunde',
      items: [
        { id: 'erster_kunde_generiert', label: 'Erster Kunde generiert' },
      ],
    },
  ],
};

// Flache Liste aller Checklisten-Items eines Überstatus.
export const checklistItems = (superStatusId) =>
  (CHECKLISTS[superStatusId] || []).flatMap((sec) => sec.items);

// Kundensichtbare Checklisten-Punkte (shared: true) über ALLE Phasen, in Reihenfolge.
// Diese werden auf der Research-Seite (Setup-Details) als Fortschritt angezeigt und
// teilen sich denselben Status wie die Team-Dashboard-Checkliste (customers/<slug>/checklist).
// Quelle der Wahrheit bleibt das Kundenobjekt – die Research-Seite spiegelt nur.
export const SHARED_CHECKLIST_ITEMS = Object.keys(CHECKLISTS).flatMap((sid) =>
  checklistItems(sid)
    .filter((it) => it.shared)
    .map((it) => ({
      id: it.id,
      label: it.label,
      clientLabel: it.clientLabel || it.label,
      superStatusId: sid,
    }))
);

// Set der kundensichtbaren Item-IDs (schneller Lookup, z. B. für Badges).
export const SHARED_CHECKLIST_IDS = new Set(SHARED_CHECKLIST_ITEMS.map((it) => it.id));

// Kumulierte Checklisten-Items: ALLE Items der regulären Pipeline vom Start bis
// einschließlich des angegebenen Überstatus. So lässt sich auf einen Blick zählen,
// wie viele Punkte über alle bisherigen Phasen hinweg noch offen sind (nicht nur
// die der aktuellen Phase). Doppelte Item-IDs werden zusammengefasst.
export function cumulativeChecklistItems(superStatusId) {
  const idx = REGULAR_SUPER_STATUS_ORDER.indexOf(superStatusId);
  const ids = idx < 0 ? [superStatusId] : REGULAR_SUPER_STATUS_ORDER.slice(0, idx + 1);
  const seen = new Set();
  const out = [];
  ids.forEach((id) => {
    checklistItems(id).forEach((it) => {
      if (seen.has(it.id)) return;
      seen.add(it.id);
      out.push(it);
    });
  });
  return out;
}

// Welche dieser Items sind laut Report-Daten bereits vorhanden? -> { itemId: bool }
export function computeAutoChecklist(superStatusId, report) {
  const out = {};
  checklistItems(superStatusId).forEach((it) => {
    if (typeof it.auto === 'function') {
      try { out[it.id] = !!it.auto(report); } catch (_) { out[it.id] = false; }
    }
  });
  return out;
}

// ─────────────────────────────────────────────────────────────
//  Präsentationstermin-Erkennung & Countdown
//
//  Der Termin steckt oft schon in den eingehenden Infos (Info-Dump / API).
//  detectPresentationDate() liest ihn deterministisch aus Freitext heraus und
//  liefert einen ms-Timestamp. Wird beim Anlegen am Container festgeschrieben
//  (presentationAt) – daraus berechnet das Board den Countdown.
// ─────────────────────────────────────────────────────────────

const MONTHS_DE = {
  januar: 0, jaenner: 0, 'jänner': 0, jan: 0,
  februar: 1, feb: 1,
  'märz': 2, maerz: 2, mrz: 2,
  april: 3, apr: 3,
  mai: 4,
  juni: 5, jun: 5,
  juli: 6, jul: 6,
  august: 7, aug: 7,
  september: 8, sep: 8, sept: 8,
  oktober: 9, okt: 9,
  november: 10, nov: 10,
  dezember: 11, dez: 11,
};

const PRES_KEYWORDS = [
  'präsentation', 'praesentation', 'präsentations', 'praesentations',
  'termin', 'call', 'meeting', 'demo', 'gespräch', 'gespraech',
  'vorstellung', 'zoom', 'meet', 'teams',
];

const buildDate = (y, mon, d, hh, mm) => {
  if (mon < 0 || mon > 11 || d < 1 || d > 31) return null;
  const date = new Date(y, mon, d, hh != null ? hh : 9, mm != null ? mm : 0, 0, 0);
  if (isNaN(date.getTime()) || date.getDate() !== d) return null;
  return date;
};

export function detectPresentationDate(text, nowMs = Date.now()) {
  if (!text || typeof text !== 'string') return null;
  const lower = text.toLowerCase();
  const candidates = [];

  const findTimeNear = (idx, len) => {
    const win = lower.slice(idx + len, idx + len + 28);
    let t = win.match(/(\d{1,2})[:.](\d{2})\s*(uhr)?/);
    if (t) return { hh: +t[1], mm: +t[2] };
    t = win.match(/(\d{1,2})\s*uhr/);
    if (t) return { hh: +t[1], mm: 0 };
    return null;
  };
  const push = (index, date) => { if (date) candidates.push({ index, ms: date.getTime() }); };

  let m;
  // dd.mm.yyyy / dd.mm.yy
  const re1 = /(\d{1,2})\.(\d{1,2})\.(\d{2,4})/g;
  while ((m = re1.exec(lower))) {
    let y = +m[3]; if (y < 100) y += 2000;
    const tm = findTimeNear(m.index, m[0].length);
    const d = buildDate(y, +m[2] - 1, +m[1], tm && tm.hh, tm && tm.mm);
    push(m.index, d);
  }
  // ISO yyyy-mm-dd
  const re2 = /(\d{4})-(\d{2})-(\d{2})/g;
  while ((m = re2.exec(lower))) {
    const tm = findTimeNear(m.index, m[0].length);
    push(m.index, buildDate(+m[1], +m[2] - 1, +m[3], tm && tm.hh, tm && tm.mm));
  }
  // dd. Monat [yyyy]
  const re3 = /(\d{1,2})\.?\s*(januar|jaenner|jänner|februar|märz|maerz|mrz|april|mai|juni|juli|august|september|sept|oktober|november|dezember|jan|feb|apr|jun|jul|aug|sep|okt|nov|dez)\.?(?:\s+(\d{4}))?/g;
  while ((m = re3.exec(lower))) {
    const mon = MONTHS_DE[m[2]];
    if (mon === undefined) continue;
    const year = m[3] ? +m[3] : new Date(nowMs).getFullYear();
    let d = buildDate(year, mon, +m[1]);
    if (d && !m[3] && d.getTime() < nowMs - 86400000) d = buildDate(year + 1, mon, +m[1]);
    const tm = findTimeNear(m.index, m[0].length);
    if (d && tm) d.setHours(tm.hh, tm.mm, 0, 0);
    push(m.index, d);
  }
  // relativ: "in X Tagen/Wochen"
  const re4 = /in\s+(\d{1,2})\s*(tagen|tage|tag|wochen|woche)/g;
  while ((m = re4.exec(lower))) {
    const mult = m[2].indexOf('woche') === 0 ? 7 : 1;
    const d = new Date(nowMs + (+m[1]) * mult * 86400000);
    d.setHours(9, 0, 0, 0);
    push(m.index, d);
  }

  if (candidates.length === 0) return null;

  const kwPos = [];
  PRES_KEYWORDS.forEach((kw) => {
    let i = lower.indexOf(kw);
    while (i !== -1) { kwPos.push(i); i = lower.indexOf(kw, i + 1); }
  });
  const nearestKw = (idx) => (kwPos.length ? Math.min(...kwPos.map((p) => Math.abs(p - idx))) : 99999);

  candidates.forEach((c) => {
    const future = c.ms >= nowMs - 86400000;
    c.score = (future ? 0 : 1e7) + nearestKw(c.index);
  });
  candidates.sort((a, b) => a.score - b.score);
  return candidates[0].ms;
}

// Countdown-Text + Ton für die UI. tone: 'far' | 'soon' | 'today' | 'over'.
export function formatCountdown(at, nowMs = Date.now()) {
  if (!at || typeof at !== 'number') return null;
  const dayMs = 86400000;
  const diff = at - nowMs;
  const startToday = new Date(nowMs); startToday.setHours(0, 0, 0, 0);
  const startTarget = new Date(at); startTarget.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startTarget.getTime() - startToday.getTime()) / dayMs);
  if (diff < 0) {
    if (dayDiff === 0) return { label: 'heute vorbei', tone: 'over', dayDiff };
    const overDays = -dayDiff;
    return { label: overDays === 1 ? 'gestern' : `vor ${overDays} Tagen`, tone: 'over', dayDiff };
  }
  if (dayDiff === 0) return { label: 'heute', tone: 'today', dayDiff };
  if (dayDiff === 1) return { label: 'morgen', tone: 'soon', dayDiff };
  if (dayDiff <= 3) return { label: `in ${dayDiff} Tagen`, tone: 'soon', dayDiff };
  return { label: `in ${dayDiff} Tagen`, tone: 'far', dayDiff };
}

// Menschlich lesbare Dauer (für Verweildauer & Zeit-bis-erster-Kunde).
export function formatDuration(ms) {
  if (!ms || ms < 0) ms = 0;
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} Std ${min % 60} Min`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'Tag' : 'Tage'} ${h % 24} Std`;
}

// Verweildauern je Status: gespeicherte Summen + die laufende Zeit des aktiven
// Status. Gibt eine sortierte Liste zurück (längste zuerst) + den aktiven Status.
export function computeStatusDurations(customer, nowMs = Date.now()) {
  const stored = customer.statusDurations || {};
  const active = customer.superStatus || DEFAULT_SUPER_STATUS;
  const enteredAt = typeof customer.statusEnteredAt === 'number'
    ? customer.statusEnteredAt
    : (typeof customer.createdAt === 'number' ? customer.createdAt : nowMs);
  const totals = {};
  COLUMNS.forEach((c) => { totals[c.superStatusId] = stored[c.superStatusId] || 0; });
  // auch unbekannte (alte) Status mitnehmen
  Object.keys(stored).forEach((k) => { if (totals[k] === undefined) totals[k] = stored[k]; });
  totals[active] = (totals[active] || 0) + Math.max(0, nowMs - enteredAt);
  const list = Object.keys(totals)
    .filter((k) => totals[k] > 0 || k === active)
    .map((k) => {
      const col = COLUMNS.find((c) => c.superStatusId === k);
      return { id: k, label: col ? col.superStatusLabel : k, ms: totals[k], active: k === active };
    });
  list.sort((a, b) => b.ms - a.ms);
  return list;
}

// Baut das operative Kundenobjekt zu einem Report. Bewusst getrennt von den
// Report-Details: hier nur die operative Sicht (Status, bezahlt, Angebot …).
// Über reportSlug gelangt man per Link zur Detail-Seite (/research/<slug>).
export function buildCustomerObject({ slug, companyName, createdBy, source }) {
  return {
    reportSlug: slug,
    companyName: companyName || '(ohne Name)',
    phase: DEFAULT_PHASE,
    superStatus: DEFAULT_SUPER_STATUS,
    subStatus: '',
    paid: false,
    offerSent: false,
    notes: '',
    createdBy: createdBy || 'unknown',
    source: source || 'dashboard',
  };
}
